import { IS_APP } from "@/__main__/constants.mjs";
import mainRefs from "@/__main__/refs.mjs";
import clone from "@/util/clone.mjs";
import globals from "@/util/global-whitelist.mjs";

const memoizedBlitzMessage = IS_APP
  ? globals.__BLITZ__.getBlitzMessage()
  : () => Promise.resolve();

let lastReadEvent = null;

// This needs to be empty on initialization. Only data from blitz-core.
const _events = (globals.__BLITZ_TEST_MOCKS__?.EVENTS ?? {}) as BlitzEvents;

// This is the only event that is hardcoded. Do not try to manually hardcode this
// to the private `_events` object, it could lead to erroneous checks for events.
const EVENT_TYPES = "meta/EVENT_TYPES";

export const EVENTS = new Proxy(_events, {
  get(target, key, receiver) {
    lastReadEvent = key;
    return Reflect.get(target, key, receiver);
  },
});

export const initEvents = !IS_APP
  ? Promise.resolve()
  : memoizedBlitzMessage(EVENT_TYPES).then((result: BlitzEvents | void) => {
      if (!result) return;

      // Do not try to rename any of these, we want these to be searchable in blitz-core.
      for (const key in result) {
        _events[key] = result[key];
      }
    });

const blitzMessage: BlitzMessaging["blitzMessage"] = async (
  type: symbol | string,
  value?: unknown,
) => {
  if (typeof type === "symbol")
    throw new Error(
      `Event type "${type.description}" should not be using a Symbol`,
    );

  if (mainRefs.mockBlitzMessage[type]) {
    return mainRefs.mockBlitzMessage[type](type, value);
  }

  if (!IS_APP)
    // This is a no-op outside of the app.
    return Promise.resolve();

  const memoEvent = lastReadEvent;
  await initEvents;
  const memoValue = _events[memoEvent];

  if (!memoValue) throw new Error(`Event type "${memoEvent}" is missing.`);
  if (type && type !== memoValue)
    throw new Error(
      `Hard-coding event type "${type}" is not allowed, must read from EVENTS. Last read event: "${memoEvent}"`,
    );

  // Why clone? Sometimes the object we wanna send may not be serializable type
  // like Proxy. clone util will turn it into plain objects.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const serializedValue =
    value && typeof value === "object" ? clone(value) : value;
  return (memoizedBlitzMessage as any)(memoValue, serializedValue);
  /* eslint-enable @typescript-eslint/no-explicit-any */
};

export default blitzMessage;

const internalHandlerMap = new Map();

export const handleMessage: BlitzMessaging["handleMessage"] = (
  type,
  callback,
) => {
  if (!type) return;

  if (globals.__BLITZ_TEST_MOCKS__) {
    return globals.__BLITZ_TEST_MOCKS__.handleMessage(type, callback);
  }

  if (!IS_APP) {
    return Promise.resolve();
  }

  internalHandlerMap.set(type, callback);

  return globals.__BLITZ__.handleMessage(type, callback);
};

function emitMessage(type, value) {
  const fn = internalHandlerMap.get(type);

  if (!fn) {
    throw new Error(`No handler found for event type "${type}"`);
  }

  return fn(value);
}

globals.__BLITZ_DEV__.blitzMessage = blitzMessage;
globals.__BLITZ_DEV__.EVENTS = EVENTS;
globals.__BLITZ_DEV__.internalHandlerMap = internalHandlerMap;
globals.__BLITZ_DEV__.emitMessage = emitMessage;
